import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { X, Loader2, Square, PenLine, Spline, Undo2, Check } from 'lucide-react';
import { mapSnapshot } from '../lib/exporters';
import { uid } from '../lib/model';
import { openSketch } from './SketchEditor';
import { uiZoom } from '../lib/uiScale';
import { roofMask, polyArea } from '../lib/roofMask';
import '../lib/i18nRoofDraw';

export const openRoofFrame = () => window.dispatchEvent(new Event('giss-roof-frame'));
const dims = (src) => new Promise((res) => { const i = new Image(); i.onload = () => res([i.width, i.height]); i.src = src; });
const MODES = [['rect', Square], ['free', PenLine], ['poly', Spline]];

// mark a building in the aerial map (frame, freehand or points) -> true-to-scale roof plan + sketch with the roof outline
export default function RoofFrame({ t, project, updateProject }) {
  const [box, setBox] = useState(null), [mode, setMode] = useState('free');
  const [r, setR] = useState(null), [pts, setPts] = useState([]), [hover, setHover] = useState(null);
  const [busy, setBusy] = useState(false);
  const lastTap = useRef(0);
  useEffect(() => {
    const h = () => {
      const v = window.__gissMapView?.(); if (!v) return;
      v.m.setBearing?.(0);
      const el = v.m.getContainer();
      el.scrollIntoView({ block: 'center' });
      setTimeout(() => { const b = el.getBoundingClientRect(), z = uiZoom(); setBox({ left: b.left / z, top: b.top / z, width: b.width / z, height: b.height / z }); setR(null); setPts([]); }, 450);
    };
    window.addEventListener('giss-roof-frame', h); return () => window.removeEventListener('giss-roof-frame', h);
  }, []);
  if (!box) return null;
  // Leaflet maps the pointer to map pixels incl. the app text-size zoom (body zoom)
  const at = (e) => { const p = window.__gissMapView().m.mouseEventToContainerPoint(e); return [p.x, p.y]; };
  const take = async (q, outline) => {
    const v = window.__gissMapView(), w0 = Math.abs(q[2] - q[0]), h0 = Math.abs(q[3] - q[1]);
    if (w0 < 30 || h0 < 30) { setR(null); setPts([]); toast.error(t.rfSmall); return; }
    setBusy(true);
    const x0 = Math.min(q[0], q[2]), y0 = Math.min(q[1], q[3]);
    const c = v.m.containerPointToLatLng([x0 + w0 / 2, y0 + h0 / 2]);
    let z = Math.min(19, Math.round(v.z));
    while (z > 1 && Math.max(w0, h0) * 2 ** (z - v.z) > 2400) z -= 1;
    const s = 2 ** (z - v.z), W = Math.round(w0 * s), H = Math.round(h0 * s);
    let src = await mapSnapshot({ lat: c.lat, lon: c.lng, zoom: z }, [], { noCross: true }, W, H);
    if (!src) { setBusy(false); toast.error(t.fpFail); return; }
    const [w, h] = await dims(src), mpp = (156543.03392 * Math.cos((c.lat * Math.PI) / 180)) / 2 ** z, wm = Math.round(w * mpp * 10) / 10, hm = Math.round(h * mpp * 10) / 10;
    const items = [];
    let area = null;
    if (outline) {
      const px = outline.map(([x, y]) => [((x - x0) * s * w) / W, ((y - y0) * s * h) / H]);
      src = await roofMask(src, px);
      const m = px.map(([x, y]) => [+(x * mpp).toFixed(2), +(y * mpp).toFixed(2)]);
      area = polyArea(m);
      items.push({ id: uid('sz'), k: 'free', roof: true, name: t.rfRoofArea, pts: [...m, m[0]] });
    }
    setBusy(false);
    const pl = { id: uid('plan'), name: `${t.fpRoofPlan} ${(project.plans || []).length + 1}`, src, w, h, mpp, lat: c.lat, lon: c.lng, sat: true, ...(area ? { roofArea: +area.toFixed(1) } : {}), sketch: { W: wm, H: hm, items, bg: { src, w, h, wm } } };
    updateProject((pr) => ({ ...pr, plans: [...(pr.plans || []), pl], planOn: pl.id }));
    setBox(null); setPts([]);
    toast.success(area ? `${t.fpFromMapOk} · ${t.rfRoofArea} ${area.toFixed(1).replace('.', ',')} m²` : t.fpFromMapOk);
    setTimeout(() => openSketch(pl.id), 300);
  };
  // closed outline -> bounding box with a margin so the roof edge stays visible
  const finish = (list) => {
    if (list.length < 3) { setPts([]); toast.error(t.rfFew); return; }
    const xs = list.map((p) => p[0]), ys = list.map((p) => p[1]), g = 14;
    if (Math.max(...xs) - Math.min(...xs) < 20 || Math.max(...ys) - Math.min(...ys) < 20) { setPts([]); toast.error(t.rfSmall); return; }
    take([Math.max(0, Math.min(...xs) - g), Math.max(0, Math.min(...ys) - g), Math.min(box.width, Math.max(...xs) + g), Math.min(box.height, Math.max(...ys) + g)], list);
  };
  const down = (e) => {
    if (busy) return;
    const p = at(e);
    if (mode === 'poly') {
      // double-click (two taps within 350 ms at the same spot) closes the outline; PointerEvent.detail is 0 in Chromium
      const now = Date.now(), l = pts[pts.length - 1], dbl = l && now - lastTap.current < 350 && Math.hypot(p[0] - l[0], p[1] - l[1]) < 10;
      lastTap.current = now;
      if (dbl) { finish(pts); return; }
      setPts((a) => [...a, p]); return;
    }
    e.currentTarget.setPointerCapture(e.pointerId);
    if (mode === 'rect') setR([...p, ...p]); else setPts([p]);
  };
  const move = (e) => {
    if (busy) return;
    const p = at(e);
    if (mode === 'poly') { setHover(p); return; }
    if (!e.buttons) return;
    if (mode === 'rect' && r) setR([r[0], r[1], ...p]);
    if (mode === 'free' && pts.length) { const l = pts[pts.length - 1]; if (Math.hypot(p[0] - l[0], p[1] - l[1]) > 3) setPts((a) => [...a, p]); }
  };
  const up = () => {
    if (busy) return;
    if (mode === 'rect' && r) take(r);
    if (mode === 'free' && pts.length) finish(pts);
  };
  const st = mode === 'rect' && r && { left: Math.min(r[0], r[2]), top: Math.min(r[1], r[3]), width: Math.abs(r[2] - r[0]), height: Math.abs(r[3] - r[1]) };
  const line = [...pts, ...(mode === 'poly' && hover && pts.length ? [hover] : [])].map((p) => p.join(',')).join(' ');
  const stop = (e) => e.stopPropagation();
  return createPortal(
    <div className="rf-layer" data-testid="roof-frame-layer" style={{ left: box.left, top: box.top, width: box.width, height: box.height }} onPointerDown={down} onPointerMove={move} onPointerUp={up}>
      <div className="rf-hint" data-testid="roof-frame-hint" onPointerDown={stop}>
        {busy ? <Loader2 size={16} className="spin" /> : null} {t[`rfHint_${mode}`]}
        <span className="chip-group rf-modes">{MODES.map(([k, I]) => <button key={k} type="button" className={`chip seg ${mode === k ? 'on' : ''}`} data-testid={`roof-mode-${k}`} onClick={() => { setMode(k); setPts([]); setR(null); }}><I size={14} /> {t[`rfMode_${k}`]}</button>)}</span>
        {mode === 'poly' && <>
          <button type="button" className="btn btn-outline btn-sm" data-testid="roof-poly-undo" disabled={!pts.length} onClick={() => setPts((a) => a.slice(0, -1))}><Undo2 size={14} /> {t.rfUndo}</button>
          <button type="button" className="btn btn-green btn-sm" data-testid="roof-poly-done" disabled={pts.length < 3 || busy} onClick={() => finish(pts)}><Check size={14} /> {t.rfDone} ({pts.length})</button>
        </>}
        <button type="button" className="btn btn-outline btn-sm" data-testid="roof-frame-cancel" onClick={() => { setBox(null); setPts([]); }}><X size={14} /> {t.rfCancel}</button>
      </div>
      {st && <div className="rf-rect" data-testid="roof-frame-rect" style={st} />}
      {mode !== 'rect' && pts.length > 0 && <svg className="rf-svg" data-testid="roof-outline" width={box.width} height={box.height}><polygon points={line} fill="rgba(255,106,19,0.18)" stroke="#ff6a13" strokeWidth="3" strokeLinejoin="round" />{mode === 'poly' && pts.map((p, i) => <circle key={i} cx={p[0]} cy={p[1]} r="5" fill="#fff" stroke="#ff6a13" strokeWidth="2.5" />)}</svg>}
    </div>,
    document.body,
  );
}
